import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { DateRange as DateRageIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

interface DatePickerProps {
  onChange?: (arg: Dayjs | null) => void;
  value: Dayjs | null;
  allowMaxDate?: boolean;
  disabled?: boolean;
  minDate?: Dayjs;
  [key: string]: any;
}

export default function BasicDatePicker({
  allowMaxDate = false,
  disabled = false,
  value,
  onChange = () => {},
  minDate,
  ...props
}: DatePickerProps) {
  const theme = useTheme();
  const [open, setOpen] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [adornmentFocus, setAdornmentFocus] = React.useState<boolean>(false);

  React.useEffect(() => {
    const handleWindowBlur = () => {
      setOpen(false);
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        minDate={minDate ? minDate : undefined}
        disabled={disabled}
        inputFormat="DD/MM/YYYY"
        open={open}
        value={value}
        onChange={(newValue) => {
          onChange(newValue);
        }}
        onError={(e) => {
          if (e === null) setError(false);
          else setError(true);
        }}
        InputProps={{
          sx: {
            pl: 0,
          },
        }}
        onClose={() => setOpen(false)}
        maxDate={allowMaxDate ? undefined : dayjs()}
        InputAdornmentProps={{
          position: 'start',
          onFocus: () => setAdornmentFocus(true),
          onBlur: () => setAdornmentFocus(false),
          sx: {
            '.MuiIconButton-root': { margin: 0 },
            backgroundColor: theme.palette.background.default,
            height: '100%',
            width: '32px',
            padding: 0,
            maxHeight: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px ${theme.palette.background.default} solid`,
          },
        }}
        PopperProps={{
          placement: 'bottom-start',
        }}
        components={{
          OpenPickerIcon: () => <DateRageIcon />,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onTouchStart={() => setOpen(true)}
            fullWidth
            onClick={() => setOpen(true)}
            sx={{
              svg: { '&:hover': {}, border: adornmentFocus ? `1px solid ${theme.palette.primary.light}` : '' },
              '.MuiOutlinedInput-input': { pt: 1, pb: 1, color: error ? 'red' : 'black' },
            }}
          />
        )}
        {...props}
      />
    </LocalizationProvider>
  );
}
